/**
 * 预览页:这篇是「官网栏目」的一期时,显示栏目与计划时间 +「确认并按计划发布」(1009 二期)。
 * 没审过的栏目稿绝不自动发 —— 这里点了才建发布记录;导出前检查有确认项时要先勾「我已确认」。
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";

import type { ExportPreflight } from "../api/reporterService";
import { reporterColumnService } from "../api/reporterColumnService";
import { COLUMN_ITEM_LABEL, plannedText } from "../model/siteColumn";
import { articlePublicationsKey } from "../model/sitePublication";

export function ColumnConfirmBox({
  draftId,
  articleId,
  preflight,
  blockedReason,
}: {
  draftId: number;
  articleId: number;
  preflight: ExportPreflight | undefined;
  blockedReason: string | null;
}) {
  const qc = useQueryClient();
  const key = ["reporter-column-item-by-draft", String(draftId)];
  const q = useQuery({ queryKey: key, queryFn: () => reporterColumnService.byDraft(draftId) });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const item = q.data;
  if (!item) return null;
  const confirmations = preflight?.confirmation ?? [];
  const needConfirm = confirmations.length > 0 && !confirmed;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await reporterColumnService.confirm(item.id, confirmations.length > 0);
      qc.setQueryData(key, r);
      await qc.invalidateQueries({ queryKey: articlePublicationsKey(articleId) });
      toast({
        title: r.status === "FAILED" ? "没有发出去" : "已确认,按计划发布",
        description: r.lastError ?? undefined,
      });
    } catch (e) {
      toast(errorToast(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-2 rounded-lg border border-primary/40 bg-primary/5 p-2.5 text-2xs leading-relaxed"
      data-column-confirm=""
    >
      <div className="flex items-center gap-1.5 text-xs font-semibold">
        <CalendarCheck className="h-3.5 w-3.5" />
        官网栏目「{item.columnName ?? "栏目"}」· {plannedText(item.plannedAt)}
        <span className="ml-auto font-normal text-muted-foreground">
          {COLUMN_ITEM_LABEL[item.status]}
        </span>
      </div>
      {item.status === "REVIEW" ? (
        <>
          <p className="mt-1 text-muted-foreground">
            这一期已写好,确认后到点自动发到官网;不确认到点不会发。
          </p>
          {item.warning && (
            <p
              className="mt-1 rounded border border-warning/50 bg-warning/10 px-1.5 py-1 font-medium text-warning"
              data-column-confirm-warning=""
            >
              {item.warning}
            </p>
          )}
          {confirmations.length > 0 && (
            <label className="mt-1 flex items-center gap-1.5">
              <input
                type="checkbox"
                aria-label="栏目稿我已确认"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              已看过确认项,照发
            </label>
          )}
          <Button
            size="sm"
            className="mt-1.5 w-full"
            disabled={busy || needConfirm || !!blockedReason}
            title={blockedReason ?? undefined}
            onClick={() => void submit()}
            data-column-confirm-submit=""
          >
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            确认并按计划发布
          </Button>
        </>
      ) : (
        item.lastError && <p className="mt-1 text-muted-foreground">{item.lastError}</p>
      )}
    </div>
  );
}
